import React, { useState, useEffect, useCallback } from 'react';
import BatchManagement from './products/BatchManagement';
import apiService from '../services/api';

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  images: string[];
  metadata: Record<string, string>;
  prices: {
    id: string;
    currency: string;
    unit_amount: number;
    recurring?: {
      interval: string;
      interval_count: number;
    };
  }[];
  created: string;
  active: boolean;
  visible?: boolean;
}

interface ProductFormData {
  name: string;
  description: string;
  images: string[];
  metadata: Record<string, string>;
  prices: {
    currency: string;
    unitAmount: number;
    recurring?: {
      interval: string;
      intervalCount: number;
    };
  }[];
}

interface ProductManagementSectionProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const ProductManagementSection: React.FC<ProductManagementSectionProps> = ({
  activeTab,
  setActiveTab
}) => {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<StripeProduct | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    images: [''],
    metadata: {},
    prices: [{ currency: 'usd', unitAmount: 0 }]
  });

  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('ProductManagementSection error:', error);
      setComponentError(error.message);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getStripeProducts();
      if (response.success && response.data) {
        setProducts(response.data.products || []);
      } else {
        setError(response.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleInputChange = (field: keyof ProductFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleProductVisibility = async (productId: string, currentVisibility: boolean) => {
    try {
      const response = await apiService.updateProductVisibility(productId, !currentVisibility);
      if (response.success) {
        setProducts(prev => prev.map(product =>
          product.id === productId
            ? { ...product, visible: !currentVisibility }
            : product
        ));
      } else {
        setError(response.message || 'Failed to update product visibility');
      }
    } catch (error) {
      console.error('Error updating product visibility:', error);
      setError('Failed to update product visibility. Please try again.');
    }
  };

  const toggleProductStatus = async (productId: string, currentStatus: boolean) => {
    try {
      const response = await apiService.updateProduct(productId, { active: !currentStatus });
      if (response.success) {
        setProducts(prev => prev.map(product =>
          product.id === productId
            ? { ...product, active: !currentStatus }
            : product
        ));
      } else {
        setError(response.message || 'Failed to update product status');
      }
    } catch (error) {
      console.error('Error updating product status:', error);
      setError('Failed to update product status. Please try again.');
    }
  };

  const handlePriceChange = (index: number, field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      prices: prev.prices.map((price, i) =>
        i === index ? { ...price, [field]: value } : price
      )
    }));
  };

  const addPrice = () => {
    setFormData(prev => ({
      ...prev,
      prices: [...prev.prices, { currency: 'usd', unitAmount: 0 }]
    }));
  };

  const removePrice = (index: number) => {
    setFormData(prev => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index)
    }));
  };

  const addImageField = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const removeImageField = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const addMetadataField = () => {
    setFormData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, '': '' }
    }));
  };

  const removeMetadataField = (key: string) => {
    setFormData(prev => {
      const newMetadata = { ...prev.metadata };
      delete newMetadata[key];
      return { ...prev, metadata: newMetadata };
    });
  };

  const handleMetadataChange = (oldKey: string, newKey: string, value: string) => {
    setFormData(prev => {
      const newMetadata = { ...prev.metadata };
      if (oldKey !== newKey) {
        delete newMetadata[oldKey];
      }

      // Compared inline rather than through a helper. Same rule as before,
      // but the guard now sits at the assignment where both a reader and a
      // static analyser can see that the key reaching it is not one of the
      // three that would write to the prototype instead of the object.
      if (newKey !== '__proto__' && newKey !== 'constructor' && newKey !== 'prototype') {
        newMetadata[newKey] = value;
      }

      return { ...prev, metadata: newMetadata };
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      images: [''],
      metadata: {},
      prices: [{ currency: 'usd', unitAmount: 0 }]
    });
    setEditingProduct(null);
    setShowCreateForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!formData.name.trim()) {
        throw new Error('Product name is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Product description is required');
      }
      if (formData.prices.length === 0) {
        throw new Error('At least one price is required');
      }
      if (formData.prices.some(p => p.unitAmount <= 0)) {
        throw new Error('All prices must be greater than 0');
      }

      if (editingProduct) {
        const response = await apiService.updateProduct(editingProduct.id, {
          name: formData.name,
          description: formData.description,
          images: formData.images.filter(img => img.trim()),
          metadata: formData.metadata,
        });

        if (!response.success) {
          throw new Error(response.message || 'Failed to update product');
        }
      } else {
        const response = await apiService.createProduct({
          name: formData.name,
          description: formData.description,
          images: formData.images.filter(img => img.trim()),
          metadata: formData.metadata,
          prices: formData.prices,
        });

        if (!response.success) {
          throw new Error(response.message || 'Failed to create product');
        }
      }

      await fetchProducts();
      resetForm();
      alert(editingProduct ? 'Product updated successfully!' : 'Product created successfully!');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (product: StripeProduct) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      images: product.images.length > 0 ? product.images : [''],
      metadata: product.metadata || {},
      prices: product.prices.map(p => ({
        currency: p.currency,
        unitAmount: p.unit_amount / 100,
        recurring: p.recurring ? {
          interval: p.recurring.interval,
          intervalCount: p.recurring.interval_count
        } : undefined
      }))
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (product: StripeProduct) => {
    if (!confirm(`Are you sure you want to archive "${product.name}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.deleteProduct(product.id);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete product');
      }

      await fetchProducts();
      alert('Product archived successfully!');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (componentError) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">⚠️</div>
        <h2 className="text-2xl font-title font-bold text-deep-navy mb-2">Component Error</h2>
        <p className="text-brand-red mb-4">An error occurred while loading this section.</p>
        <p className="text-gray-500 text-sm mb-4">Error: {componentError}</p>
        <button
          onClick={() => setComponentError(null)}
          className="bg-brand-red hover:bg-brand-red-light text-white font-bold px-6 py-3 rounded-xl transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Product Management Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-title font-bold text-deep-navy">Quick Actions</h3>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                Create New Product
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => setActiveTab('products')}
                className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                View All Products
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className="bg-ice-teal-dark hover:bg-ice-teal text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                Manage Pricing
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                Product Analytics
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Products</h3>
              <div className="text-3xl font-bold text-brand-blue">{products.length}</div>
              <div className="text-gray-500 text-sm">In catalog</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Active Products</h3>
              <div className="text-3xl font-bold text-brand-blue-light">
                {products.filter(p => p.active).length}
              </div>
              <div className="text-gray-500 text-sm">Currently available</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Prices</h3>
              <div className="text-3xl font-bold text-scarf-green">
                {products.reduce((sum, p) => sum + p.prices.length, 0)}
              </div>
              <div className="text-gray-500 text-sm">Across all products</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Last Updated</h3>
              <div className="text-2xl font-bold text-gray-600">
                {products.length > 0 ?
                  new Date(Math.max(...products.map(p => new Date(p.created).getTime()))).toLocaleDateString() :
                  'Never'
                }
              </div>
              <div className="text-gray-500 text-sm">Most recent change</div>
            </div>
          </div>
        </div>
      )}

      {/* Products List Tab */}
      {activeTab === 'products' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-title font-bold text-deep-navy">All Products</h3>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-xl transition-colors"
              >
                Add Product
              </button>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-600 text-lg">Loading products...</div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg">No products found</div>
                <div className="text-gray-400 text-sm mt-2">Create your first product to get started</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ice-teal-light">
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Product</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Description</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Prices</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Visible</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Created</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            {product.images && product.images.length > 0 && (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <div>
                              <div className="font-bold text-deep-navy">{product.name}</div>
                              <div className="text-gray-500 text-sm">{product.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-gray-700 text-sm max-w-xs truncate">
                            {product.description}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            {product.prices.map((price) => (
                              <div key={price.id} className="text-sm">
                                <span className="text-brand-blue font-bold">
                                  {formatCurrency(price.unit_amount)}
                                </span>
                                <span className="text-gray-500 ml-2">{price.currency.toUpperCase()}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => toggleProductStatus(product.id, product.active ?? true)}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                              product.active !== false
                                ? 'bg-ice-teal/30 text-brand-blue hover:bg-ice-teal/50'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {product.active !== false ? 'ACTIVE' : 'INACTIVE'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => toggleProductVisibility(product.id, product.visible ?? true)}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                              product.visible !== false
                                ? 'bg-ice-teal/30 text-brand-blue hover:bg-ice-teal/50'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {product.visible !== false ? 'VISIBLE' : 'HIDDEN'}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-sm">
                          {new Date(product.created).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => startEdit(product)}
                              className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                            >
                              Edit
                            </button>
                            <button className="px-2 py-1 bg-brand-blue hover:bg-brand-blue-dark text-white rounded text-xs font-bold transition-colors">
                              View
                            </button>
                            <button
                              onClick={() => handleDelete(product)}
                              className="px-2 py-1 bg-brand-red hover:bg-brand-red-light text-white rounded text-xs font-bold transition-colors"
                            >
                              Archive
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'batches' && <BatchManagement />}

      {activeTab === 'pricing' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Price Points</h3>
              <div className="text-3xl font-bold text-brand-blue">
                {products.reduce((sum, p) => sum + p.prices.length, 0)}
              </div>
              <div className="text-gray-500 text-sm">Across all products</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Currencies Supported</h3>
              <div className="text-3xl font-bold text-brand-blue-light">
                {new Set(products.flatMap(p => p.prices.map(price => price.currency))).size}
              </div>
              <div className="text-gray-500 text-sm">Different currencies</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Average Price</h3>
              <div className="text-3xl font-bold text-scarf-green">
                ${(products.flatMap(p => p.prices).reduce((sum, price) => sum + price.unit_amount, 0) / Math.max(products.flatMap(p => p.prices).length, 1) / 100).toFixed(2)}
              </div>
              <div className="text-gray-500 text-sm">Across all products</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">All Pricing</h3>
            {products.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg">No products found</div>
                <div className="text-gray-400 text-sm mt-2">Create products to see pricing information</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ice-teal-light">
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Product</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Price</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Currency</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Recurring</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.flatMap(product =>
                      product.prices.map(price => (
                        <tr key={price.id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              {product.images && product.images.length > 0 && (
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="w-8 h-8 rounded object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}
                                />
                              )}
                              <div>
                                <div className="font-bold text-deep-navy">{product.name}</div>
                                <div className="text-gray-500 text-sm">{product.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-bold text-brand-blue">
                              {formatCurrency(price.unit_amount)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-frost-white rounded text-sm text-gray-700">
                              {price.currency.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700 text-sm">
                            {price.recurring ?
                              `${price.recurring.interval_count} ${price.recurring.interval}(s)` :
                              'One-time'
                            }
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => startEdit(product)}
                              className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Products</h3>
              <div className="text-3xl font-bold text-brand-blue">{products.length}</div>
              <div className="text-gray-500 text-sm">In catalog</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Active Products</h3>
              <div className="text-3xl font-bold text-brand-blue-light">
                {products.filter(p => p.active).length}
              </div>
              <div className="text-gray-500 text-sm">Currently available</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Products with Images</h3>
              <div className="text-3xl font-bold text-scarf-green">
                {products.filter(p => p.images && p.images.length > 0).length}
              </div>
              <div className="text-gray-500 text-sm">Have product images</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Products with Metadata</h3>
              <div className="text-3xl font-bold text-gray-600">
                {products.filter(p => p.metadata && Object.keys(p.metadata).length > 0).length}
              </div>
              <div className="text-gray-500 text-sm">Have additional metadata</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Product Categories</h3>
            {(() => {
              const categories = products.reduce((acc, product) => {
                const category = product.metadata?.category || 'Uncategorized';
                acc[category] = (acc[category] || 0) + 1;
                return acc;
              }, {} as Record<string, number>);

              const sortedCategories = Object.entries(categories).sort(([,a], [,b]) => b - a);

              return sortedCategories.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 text-lg">No categories found</div>
                  <div className="text-gray-400 text-sm mt-2">Add category metadata to products to see them here</div>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {sortedCategories.map(([category, count]) => (
                    <div key={category} className="bg-frost-white rounded-xl p-4">
                      <h4 className="font-bold text-deep-navy mb-2">{category}</h4>
                      <div className="text-2xl font-bold text-brand-blue">{count}</div>
                      <div className="text-gray-500 text-sm">products</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recently Updated Products</h3>
            {products.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg">No products found</div>
              </div>
            ) : (
              <div className="space-y-3">
                {products
                  .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                  .slice(0, 5)
                  .map((product) => (
                    <div key={product.id} className="flex justify-between items-center bg-frost-white rounded-xl p-3">
                      <div className="flex items-center space-x-3">
                        {product.images && product.images.length > 0 && (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-10 h-10 rounded object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="font-bold text-deep-navy">{product.name}</div>
                          <div className="text-gray-500 text-sm">
                            {product.prices.length} price{product.prices.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-500 text-sm">
                          {new Date(product.created).toLocaleDateString()}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          product.active ? 'bg-ice-teal/30 text-brand-blue' : 'bg-red-100 text-brand-red'
                        }`}>
                          {product.active ? 'ACTIVE' : 'INACTIVE'}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Product Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl border border-ice-teal-light">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-title font-bold text-deep-navy">
                {editingProduct ? 'Edit Product' : 'Create New Product'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-800 text-2xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="product-name" className="block text-deep-navy font-bold mb-2">Product Name *</label>
                  <input
                    id="product-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                    placeholder="Enter product name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="product-description" className="block text-deep-navy font-bold mb-2">Description *</label>
                  <textarea
                    id="product-description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                    placeholder="Enter product description"
                    rows={3}
                    required
                  />
                </div>
              </div>

              <div>
                {/* A group heading rather than a label element: this names a
                    list of inputs, and a label may only point at one control.
                    Each row carries its own accessible name below. */}
                <span id="product-images-label" className="block text-deep-navy font-bold mb-2">Product Images</span>
                <div className="space-y-2" role="group" aria-labelledby="product-images-label">
                  {formData.images.map((image, index) => (
                    <div key={index} className="flex space-x-2">
                      <input
                        type="url"
                        value={image}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                        placeholder="Image URL"
                        aria-label={`Image URL ${index + 1}`}
                      />
                      {formData.images.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          className="px-3 py-2 bg-brand-red hover:bg-brand-red-light text-white rounded-xl transition-colors"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addImageField}
                    className="px-4 py-2 bg-brand-blue-light hover:bg-brand-blue text-white rounded-xl transition-colors"
                  >
                    Add Image
                  </button>
                </div>
              </div>

              <div>
                {/* A group heading, as with Product Images: this names a
                    repeating set of rows, not one control. The per-row ids
                    below carry the index, because a form may hold several
                    prices and an id has to be unique in the document. */}
                <span id="product-pricing-label" className="block text-deep-navy font-bold mb-2">Pricing *</span>
                <div className="space-y-3" role="group" aria-labelledby="product-pricing-label">
                  {formData.prices.map((price, index) => (
                    <div key={index} className="grid md:grid-cols-3 gap-3 p-4 bg-frost-white rounded-xl">
                      <div>
                        <label htmlFor={`product-price-${index}-currency`} className="block text-gray-600 text-sm mb-1">Currency</label>
                        <select
                          id={`product-price-${index}-currency`}
                          value={price.currency}
                          onChange={(e) => handlePriceChange(index, 'currency', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                        >
                          <option value="usd">USD</option>
                          <option value="eur">EUR</option>
                          <option value="gbp">GBP</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`product-price-${index}-amount`} className="block text-gray-600 text-sm mb-1">Price</label>
                        <input
                          id={`product-price-${index}-amount`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={price.unitAmount}
                          onChange={(e) => handlePriceChange(index, 'unitAmount', Number.parseFloat(e.target.value))}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="flex items-end">
                        {formData.prices.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePrice(index)}
                            className="px-3 py-2 bg-brand-red hover:bg-brand-red-light text-white rounded-lg transition-colors"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addPrice}
                    className="px-4 py-2 bg-brand-blue-light hover:bg-brand-blue text-white rounded-xl transition-colors"
                  >
                    Add Price
                  </button>
                </div>
              </div>

              <div>
                {/* Group heading, for the same reason as Product Images above. */}
                <span id="product-metadata-label" className="block text-deep-navy font-bold mb-2">Metadata (Optional)</span>
                <div className="space-y-2" role="group" aria-labelledby="product-metadata-label">
                  {Object.entries(formData.metadata).map(([key, value]) => (
                    <div key={key} className="flex space-x-2">
                      <input
                        type="text"
                        value={key}
                        onChange={(e) => handleMetadataChange(key, e.target.value, value)}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                        placeholder="Key"
                        aria-label={`Metadata key: ${key}`}
                      />
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleMetadataChange(key, key, e.target.value)}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                        placeholder="Value"
                        aria-label={`Metadata value for ${key}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeMetadataField(key)}
                        className="px-3 py-2 bg-brand-red hover:bg-brand-red-light text-white rounded-lg transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addMetadataField}
                    className="px-4 py-2 bg-brand-blue-light hover:bg-brand-blue text-white rounded-xl transition-colors"
                  >
                    Add Metadata
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-ice-teal-light">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-frost-white hover:bg-ice-pale text-gray-700 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-3 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductManagementSection;
